import { preload } from 'react-dom';

import { STATIC_CDN_ORIGIN } from '@/common/constants';

/** Split out of `hero-funnel`: that module is `'use client'`, and `hero-section` renders on the
 *  server, where a function exported from a client module is only a reference. */

/** When the photo is rendered, so it is only ever preloaded then. Keep in step with `Viz`. */
export const PHOTO_MEDIA = '(min-width: 640px)';

/** The AVIF twin, or null for the one vertical whose art is an SVG. */
export function avifFor(src: string) {
  return src.endsWith('.jpg') ? src.replace(/\.jpg$/, '.avif') : null;
}

/**
 * Every URL for one hero photo, from one place, so the hint and the background cannot disagree.
 *
 * They resolve differently if computed separately: `Viz` sets the URL as a custom property, but the
 * rule consuming it lives in globals.css, which the CDN serves — and a relative `url()` in a custom
 * property resolves against the stylesheet that *uses* it, not the one that declares it. A
 * document-relative preload therefore points at the app while the background points at the CDN,
 * which wastes the hint and fetches the file twice. Only production has that split, so nothing
 * below it can catch the divergence. The CDN carries `public/` (the Dockerfile's scratch stage),
 * so resolving everything there is what keeps them equal.
 *
 * `origin` is a parameter so both branches are reachable from a test.
 */
export function heroPhotoUrls(src: string, origin: string = STATIC_CDN_ORIGIN) {
  const at = (path: string) => (origin ? `${origin}${path}` : path);
  const avif = avifFor(src);
  return {
    /** What to warm: the AVIF where there is one, since a hint typed as a format the file is not
     *  is a hint the browser discards. Always one of the two below. */
    preload: at(avif ?? src),
    avif: avif && at(avif),
    fallback: at(src),
  };
}

/**
 * Warms a hero photo as the format it actually is.
 *
 * `priority` for the one on screen at load — on a wide viewport it is the LCP element, and a
 * preload defaults to Low.
 */
export function warmHeroPhoto(src: string, { priority = false } = {}) {
  const { preload: href, avif } = heroPhotoUrls(src);
  preload(href, {
    as: 'image',
    media: PHOTO_MEDIA,
    ...(avif && { type: 'image/avif' }),
    ...(priority && { fetchPriority: 'high' as const }),
  });
}
